import { useState } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  useTheme,
  alpha,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider // Keep Divider if used in the remaining layout
} from '@mui/material';
// Removed unused icons like CompareIcon, StorefrontIcon
import BarChartIcon from '@mui/icons-material/BarChart'; // Still used as title icon potentially
import ShowChartIcon from '@mui/icons-material/ShowChart';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Legend, Tooltip as ChartTooltip
} from 'recharts'; // Removed unused chart components

// --- Mock Data Embedded ---
// Simplified mock data needed for this specific component
const mockBusiness = {
  id: 'BIZ001',
  name: 'Green Earth Cafe',
  type: 'Restaurant/Cafe',
  renewablePercentage: 100,
};

// Removed mockBuildingScore as it's not directly used in the simplified view
// const mockBuildingScore = { ... };

const mockSectorData = {
  averageScore: 78, // Still needed for radar comparison
  averageEnergyUsage: 4800, // Potentially for text insights
  averageRenewable: 42, // Still needed for radar comparison
  // Removed topPerformer and businesses list as the bar chart is gone
};
// --- End Embedded Mock Data ---

const SectorBenchmarks = () => {
  const theme = useTheme();
  // Removed tabValue state
  const [benchmarkMetric, setBenchmarkMetric] = useState<string>('energy'); // Keep if dropdown is kept

  // Use embedded mock data directly
  const business = mockBusiness;
  const sectorData = mockSectorData;
  // const businessId = mockBusiness.id; // Not needed without the bar chart logic

  const handleBenchmarkChange = (event: SelectChangeEvent) => {
    setBenchmarkMetric(event.target.value);
  };

  // Generate benchmark data (using mock values)
  const generateBenchmarkData = () => {
    if (!sectorData || !business) return [];
    // Using static values for 'A' (Your Business) for simplicity in this example
    // In a real scenario, these might come from another mock source or calculation
    return [
      { subject: 'Energy Efficiency', A: 80, B: sectorData.averageScore, fullMark: 100 },
      { subject: 'Peak Demand', A: 75, B: 65, fullMark: 100 },
      { subject: 'Renewable Energy', A: business.renewablePercentage, B: sectorData.averageRenewable, fullMark: 100 },
      { subject: 'Cost Efficiency', A: 70, B: 60, fullMark: 100 },
      { subject: 'Energy Baseline', A: 85, B: 70, fullMark: 100 }
    ];
  };

  const benchmarkData = generateBenchmarkData();

  return (
    <Box>
       {/* Removed Paper and Tabs wrapper */}
       {/* Directly render the Industry Benchmarks content */}
        <Box sx={{ p: 3 }}> {/* Add padding if needed */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    <BarChartIcon sx={{ verticalAlign: 'middle', mr: 1, color: theme.palette.primary.main }} /> {/* Optional Icon */}
                    Industry Benchmarks & Comparisons
                </Typography>
                {/* Optional: Keep the dropdown if you want to simulate changing metrics */}
                <FormControl sx={{ minWidth: 180 }} size="small">
                  <InputLabel>Benchmark Metric</InputLabel>
                  <Select value={benchmarkMetric} label="Benchmark Metric" onChange={handleBenchmarkChange}>
                    <MenuItem value="energy">Energy Efficiency</MenuItem>
                    <MenuItem value="cost">Cost Efficiency</MenuItem>
                    <MenuItem value="sustainability">Sustainability</MenuItem>
                  </Select>
                </FormControl>
            </Box>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card elevation={0} sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        {business?.name} vs. Industry Averages
                      </Typography>
                      <Box sx={{ height: 400 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart outerRadius={150} data={benchmarkData}>
                            <PolarGrid />
                            <PolarAngleAxis dataKey="subject" />
                            <PolarRadiusAxis domain={[0, 100]}/>
                            <Radar name={business?.name || 'Your Business'} dataKey="A" stroke={theme.palette.primary.main} fill={alpha(theme.palette.primary.main, 0.3)} />
                            <Radar name="Industry Average" dataKey="B" stroke={theme.palette.grey[600]} fill={alpha(theme.palette.grey[600], 0.3)} />
                            <Legend />
                            <ChartTooltip />
                          </RadarChart>
                        </ResponsiveContainer>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                   <Card elevation={0} sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 3 }}>Performance Highlights</Typography>
                      <Box sx={{ mb: 3 }}>
                        {/* Static example text */}
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          Your business is performing better than <Box component="span" sx={{ fontWeight: 700, color: theme.palette.success.main }}>75%</Box> of businesses in your sector.
                        </Typography>
                        <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}` }}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Top Performing Areas</Typography>
                          <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}><strong>Energy Baseline:</strong> 15% better than industry average</Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}><strong>Renewable Energy:</strong> {business?.renewablePercentage || 0}% (industry avg: {sectorData?.averageRenewable || 0}%)</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.1), border: `1px dashed ${alpha(theme.palette.warning.main, 0.3)}` }}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Improvement Opportunities</Typography>
                          <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}><strong>Peak Demand Management:</strong> 10% higher than top performers</Typography>
                            <Typography component="li" variant="body2"><strong>After-hours Usage:</strong> Can be reduced by up to 20%</Typography>
                          </Box>
                        </Box>
                      </Box>
                      {/* Optional Button */}
                      <Button variant="outlined" color="primary" startIcon={<ShowChartIcon />} fullWidth>View Detailed Benchmark Report</Button>
                    </CardContent>
                  </Card>
                </Grid>
            </Grid>
        </Box>
        {/* Removed the "Additional Insights" section as it was less relevant without the overall comparison */}
    </Box>
  );
};

export default SectorBenchmarks; 